import React from "react";
import {
  actionBadgeClass,
  actionLabel,
  describeChanges,
  formatDateTime,
} from "./helpers";
import useAuditLog from "./hooks/useAuditLog";

interface Props {
  active: boolean;
}

const PAGE_SIZE = 20;

const AuditTab: React.FC<Props> = ({ active }) => {
  const { entries, count, page, setPage, isLoading, error } = useAuditLog(active);
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <>
      {error && (
        <div
          className="p-4 mb-4 text-sm text-red-800 border border-red-200 rounded-lg bg-red-50"
          role="alert"
        >
          {error}
        </div>
      )}

      <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left text-gray-700">
            <thead className="text-xs uppercase text-gray-500 bg-gray-50">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Actor</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3">Change</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                    Loading audit log...
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                    No audit entries yet.
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-t border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {formatDateTime(entry.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      {entry.actor_email ?? (
                        <span className="italic text-gray-400">(deleted)</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${actionBadgeClass(entry.action)}`}
                      >
                        {actionLabel(entry.action)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {entry.target_email ?? (
                        entry.action.startsWith("data_") ? (
                          <span className="text-gray-600 font-medium">Database</span>
                        ) : (
                          <span className="italic text-gray-400">(deleted)</span>
                        )
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {describeChanges(entry)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
        <span>
          {count} entr{count === 1 ? "y" : "ies"}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
          >
            Prev
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
};

export default AuditTab;
